/**
 * Connexion à Supabase Auth.
 *
 * Appel direct au point d'accès REST plutôt qu'au SDK : il s'agit d'une seule
 * requête, et embarquer une bibliothèque entière pour ça alourdirait la page
 * sans rien apporter.
 *
 * Le mot de passe ne transite QUE vers Supabase, jamais vers nos workers. Ce
 * qu'ils reçoivent, eux, est un jeton signé dont ils vérifient la signature avec
 * une clé publique. Aucun de nos services ne voit ni ne stocke de mot de passe.
 */

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] ?? '';
const SUPABASE_ANON_KEY = import.meta.env['VITE_SUPABASE_ANON_KEY'] ?? '';

/**
 * Le jeton survit au rechargement de l'onglet, pas à sa fermeture.
 *
 * `sessionStorage` et non `localStorage` : un poste d'exploitation est partagé,
 * et une session qui survit à la fermeture du navigateur survivrait aussi au
 * départ de la personne.
 */
const CLE_SESSION = 'easy2log.admin.session';

export interface Session {
  accessToken: string;
  email: string | null;
  expiresAtMs: number;
}

export function sessionEnregistree(): Session | null {
  try {
    const brut = sessionStorage.getItem(CLE_SESSION);
    if (brut === null) return null;
    const session = JSON.parse(brut) as Session;
    return session.expiresAtMs > Date.now() ? session : null;
  } catch {
    return null;
  }
}

export function oublierSession(): void {
  sessionStorage.removeItem(CLE_SESSION);
}

export function authConfiguree(): boolean {
  return SUPABASE_URL !== '' && SUPABASE_ANON_KEY !== '';
}

export async function seConnecter(email: string, motDePasse: string): Promise<Session> {
  if (!authConfiguree()) {
    throw new Error("L'authentification n'est pas configurée (URL ou clé publique absente).");
  }

  const reponse = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', apikey: SUPABASE_ANON_KEY },
    body: JSON.stringify({ email, password: motDePasse }),
  });

  const corps = (await reponse.json()) as {
    access_token?: string;
    expires_in?: number;
    user?: { email?: string };
    error_description?: string;
    msg?: string;
  };

  if (!reponse.ok || corps.access_token === undefined) {
    throw new Error(corps.error_description ?? corps.msg ?? 'Identifiants refusés.');
  }

  const session: Session = {
    accessToken: corps.access_token,
    email: corps.user?.email ?? email,
    // Une minute de marge : un jeton qui expire pendant la requête donnerait une
    // erreur incompréhensible plutôt qu'une demande de reconnexion.
    expiresAtMs: Date.now() + (corps.expires_in ?? 3600) * 1000 - 60_000,
  };

  sessionStorage.setItem(CLE_SESSION, JSON.stringify(session));
  return session;
}
